import {cleanEntity} from "@mytour/platform";
import {
  FinalizeJardinierReportResponseSchema,
  JARDINIER_OMITTED_PHOTOS_HEADER,
  JardinierReportDocumentSchema,
} from "@mytour/shared";
import type {
  FinalizeJardinierReportResponse,
  JardinierReport,
  JardinierReportDocument,
  JardinierReportFinalizePayload,
  JardinierReportRenderPayload,
  SaveJardinierReportResponse,
} from "@mytour/shared";
import axios from "axios";

export const jardinierReportApiService = {
  async previewReport(eventId: string, payload: JardinierReportRenderPayload): Promise<Blob> {
    const result = await axios.post(
      `/api/jardinier-report/${eventId}/preview`,
      cleanEntity(payload),
      {responseType: "arraybuffer"}
    );
    return new Blob([result.data], {type: "application/pdf"});
  },

  async finalizeReport(
    eventId: string,
    payload: JardinierReportFinalizePayload
  ): Promise<FinalizeJardinierReportResponse> {
    const result = await axios.post<unknown>(
      `/api/jardinier-report/${eventId}/finalize`,
      cleanEntity(payload)
    );
    return FinalizeJardinierReportResponseSchema.parse(result.data);
  },

  async downloadFallbackFile(eventId: string): Promise<{blob: Blob; omittedPhotos: number}> {
    const result = await axios.get(`/api/jardinier-report/${eventId}/file/fallback`, {
      responseType: "arraybuffer",
    });
    const omittedPhotos = Number(result.headers[JARDINIER_OMITTED_PHOTOS_HEADER] ?? 0);
    return {
      blob: new Blob([result.data], {type: "application/pdf"}),
      omittedPhotos: Number.isSafeInteger(omittedPhotos) && omittedPhotos > 0 ? omittedPhotos : 0,
    };
  },

  async getReport(eventId: string): Promise<JardinierReport | null> {
    const result = await axios.get<JardinierReport | undefined>(
      `/api/jardinier-report/${eventId}`
    );
    return result.data ?? null;
  },

  async listDocumentsByCustomer(customerId: string): Promise<JardinierReportDocument[]> {
    const result = await axios.get<unknown[]>(`/api/jardinier-report/customer/${customerId}`);
    /**
     * `reportDate`/`eventDate` arrivent en string ISO — le schéma coerce en Date. Parsée ligne
     * à ligne, même précaution que `ramonageApiService.listDocumentsByCustomer` (#1229).
     */
    return (result.data ?? [])
      .map((raw) => JardinierReportDocumentSchema.safeParse(raw))
      .filter((parsed): parsed is {success: true; data: JardinierReportDocument} => parsed.success)
      .map((parsed) => parsed.data);
  },

  async downloadFile(eventId: string): Promise<Blob> {
    const result = await axios.get(`/api/jardinier-report/${eventId}/file`, {
      responseType: "arraybuffer",
    });
    return new Blob([result.data], {type: "application/pdf"});
  },

  async resend(
    eventId: string
  ): Promise<Pick<SaveJardinierReportResponse, "emailStatus" | "emailMessage">> {
    const result = await axios.post<
      Pick<SaveJardinierReportResponse, "emailStatus" | "emailMessage">
    >(`/api/jardinier-report/${eventId}/resend`);
    return result.data;
  },
};
